import React, { useState, useEffect, useRef, useCallback } from "react";
import "./DinoGame.css";
import { useLanguage } from "../contexts/LanguageContext";
import { useFirebase } from "../contexts/FirebaseContext";
import HighScores from "./HighScores";
import { publishDemoState } from "../utils/demoMode";

const GRAVITY = -2000; // px / s^2
const JUMP_VELOCITY = 680; // px / s
const MAX_JUMP_HEIGHT = 160;
const BASE_SPEED = 337.5; // px / s (25% faster start)
const SPEED_INCREASE = 0.1; // 10% per 100 points
const MIN_HOLE_WIDTH = 80;
const MAX_HOLE_WIDTH = 160;
const MIN_HOLE_SPACING = 420;
const MAX_HOLE_SPACING = 900;
const CLUSTER_PROBABILITY = 0.25;
const LONG_GAP_PROBABILITY = 0.1;
const DINO_X = 120;
const DINO_WIDTH = 38;
const AIRBORNE_THRESHOLD = 12;

const randomInRange = (min, max) => min + Math.random() * (max - min);

const randomHoleWidth = () => randomInRange(MIN_HOLE_WIDTH, MAX_HOLE_WIDTH);

const nextSpawnDistance = () => {
  const base = randomInRange(MIN_HOLE_SPACING, MAX_HOLE_SPACING);
  if (Math.random() < CLUSTER_PROBABILITY) {
    return base * 0.55; // occasional quick successive holes
  }
  if (Math.random() < LONG_GAP_PROBABILITY) {
    return base * 1.4; // occasional long respite
  }
  return base;
};

const initialSpawnDistance = () => nextSpawnDistance();

const DinoGame = ({ onPointsChange, autoStart = false }) => {
  const { t } = useLanguage();
  const { saveHighScore, displayName, recordGameEvent } = useFirebase();

  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [finalScore, setFinalScore] = useState(null);
  const [dinoY, setDinoY] = useState(0);
  const [isJumping, setIsJumping] = useState(false);
  const [obstacles, setObstacles] = useState([]);
  const didAutoStartRef = useRef(false);

  const animationFrameRef = useRef(null);
  const lastTimestampRef = useRef(null);
  const dinoYRef = useRef(0);
  const dinoVelocityRef = useRef(0);
  const speedRef = useRef(BASE_SPEED);
  const spawnDistanceRef = useRef(initialSpawnDistance());
  const distanceSinceLastHoleRef = useRef(0);
  const scoreRef = useRef(0);
  const scoreSavedRef = useRef(false);
  const obstaclesRef = useRef([]);

  useEffect(() => {
    publishDemoState({
      game: "dino",
      ready: gameStarted && !gameOver,
      gameOver,
      score,
      airborne: dinoY > AIRBORNE_THRESHOLD,
      dinoY,
      speed: speedRef.current,
      nearestHole: obstacles[0]
        ? { x: obstacles[0].x, width: obstacles[0].width ?? 80 }
        : null,
    });
  }, [gameStarted, gameOver, score, obstacles, dinoY]);

  const resetState = useCallback(() => {
    setGameStarted(true);
    setGameOver(false);
    setScore(0);
    setFinalScore(null);
    setIsJumping(false);
    setDinoY(0);
    setObstacles([]);
    dinoYRef.current = 0;
    dinoVelocityRef.current = 0;
    speedRef.current = BASE_SPEED;
    spawnDistanceRef.current = initialSpawnDistance();
    distanceSinceLastHoleRef.current = 0;
    scoreRef.current = 0;
    scoreSavedRef.current = false;
    obstaclesRef.current = [];
    lastTimestampRef.current = null;
  }, []);

  useEffect(() => {
    if (!autoStart || didAutoStartRef.current) return;
    didAutoStartRef.current = true;
    resetState();
  }, [autoStart, resetState]);

  const handleJump = useCallback(() => {
    if (!gameStarted || gameOver) {
      return;
    }
    if (dinoYRef.current <= AIRBORNE_THRESHOLD) {
      dinoVelocityRef.current = JUMP_VELOCITY;
      setIsJumping(true);
    }
  }, [gameStarted, gameOver]);

  useEffect(() => {
    if (!gameStarted || gameOver) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return;
    }

    const update = (timestamp) => {
      if (scoreSavedRef.current) {
        return;
      }

      if (lastTimestampRef.current == null) {
        lastTimestampRef.current = timestamp;
        animationFrameRef.current = requestAnimationFrame(update);
        return;
      }

      const delta = (timestamp - lastTimestampRef.current) / 1000; // seconds
      lastTimestampRef.current = timestamp;

      // Clamp delta to avoid huge jumps when tab is unfocused
      const dt = Math.min(delta, 0.05);

      // Update score
      scoreRef.current += dt * 25;
      const displayedScore = Math.floor(scoreRef.current);
      if (displayedScore !== score) {
        setScore(displayedScore);
      }

      // Increase speed gradually as score increases
      const speedMultiplier =
        1 + Math.floor(displayedScore / 100) * SPEED_INCREASE;
      const currentSpeed = BASE_SPEED * speedMultiplier;
      speedRef.current = currentSpeed;

      // Update dino physics
      dinoVelocityRef.current += GRAVITY * dt;
      dinoYRef.current += dinoVelocityRef.current * dt;

      if (dinoYRef.current < 0) {
        dinoYRef.current = 0;
        dinoVelocityRef.current = 0;
        if (isJumping) {
          setIsJumping(false);
        }
      } else if (dinoYRef.current > MAX_JUMP_HEIGHT) {
        dinoYRef.current = MAX_JUMP_HEIGHT;
        dinoVelocityRef.current = 0;
      }

      if (Math.abs(dinoY - dinoYRef.current) > 0.5) {
        setDinoY(dinoYRef.current);
      }

      // Update obstacles
      const updatedObstacles = obstaclesRef.current
        .map((hole) => ({
          ...hole,
          x: hole.x - currentSpeed * dt,
        }))
        .filter((hole) => hole.x > -(hole.width ?? MIN_HOLE_WIDTH));

      obstaclesRef.current = updatedObstacles;
      if (updatedObstacles.length !== obstacles.length) {
        setObstacles(updatedObstacles);
      } else if (
        updatedObstacles.some((hole, idx) => hole.x !== obstacles[idx]?.x)
      ) {
        setObstacles([...updatedObstacles]);
      }

      // Spawn holes
      distanceSinceLastHoleRef.current += currentSpeed * dt;
      if (distanceSinceLastHoleRef.current >= spawnDistanceRef.current) {
        const holeWidth = randomHoleWidth();
        const holeX = 800 + holeWidth;
        const newHole = { id: crypto.randomUUID(), x: holeX, width: holeWidth };
        obstaclesRef.current = [...obstaclesRef.current, newHole];
        setObstacles(obstaclesRef.current);
        distanceSinceLastHoleRef.current = 0;
        spawnDistanceRef.current = nextSpawnDistance();
      }

      // Collision detection (fall into hole)
      const dinoCenter = DINO_X + DINO_WIDTH / 2;
      const isAirborne = dinoYRef.current > AIRBORNE_THRESHOLD;

      const fellInHole = obstaclesRef.current.some((hole) => {
        const holeWidth = hole.width ?? MIN_HOLE_WIDTH;
        const holeLeft = hole.x - holeWidth;
        const holeRight = hole.x;
        const safetyMargin = 10;
        const holeInnerLeft = holeLeft + safetyMargin;
        const holeInnerRight = holeRight - safetyMargin;

        if (holeInnerLeft >= holeInnerRight) {
          return false;
        }

        const overHole =
          dinoCenter >= holeInnerLeft && dinoCenter <= holeInnerRight;

        if (!overHole) {
          return false;
        }

        if (isAirborne) {
          return false;
        }

        return true;
      });

      if (fellInHole) {
        const final = Math.floor(scoreRef.current);
        setGameOver(true);
        setFinalScore(final);
        scoreSavedRef.current = true;
        if (displayName) {
          saveHighScore(final);
        }
        recordGameEvent("dino_run_ended", { score: final });
        animationFrameRef.current = null;
        return;
      }

      animationFrameRef.current = requestAnimationFrame(update);
    };

    animationFrameRef.current = requestAnimationFrame(update);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameStarted, gameOver, isJumping, saveHighScore, displayName, score]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.code === "Space" || event.key === " " || event.keyCode === 32) {
        event.preventDefault();
        event.stopPropagation();

        if (!gameStarted) {
          resetState();
        } else if (!gameOver) {
          handleJump();
        }
      }
    };

    document.addEventListener("keydown", handleKeyPress, true);
    return () => {
      document.removeEventListener("keydown", handleKeyPress, true);
    };
  }, [gameStarted, gameOver, handleJump, resetState]);

  const handleAreaClick = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!gameStarted) {
      resetState();
      return;
    }

    if (!gameOver) {
      handleJump();
    }
  };

  return (
    <div className="dino-game-container">
      <div className="game-card">
        <div className="game-card-header">
          <h1 className="game-title">
            {gameOver ? t("gameOverTitle") : `🦖 ${t("dinoRun")}`}
          </h1>
          {!gameOver && (
            <div className="dino-game-info">
              <div className="score-display-dino">
                <span className="score-label">{t("score")}</span>
                <span className="score-value">🏃 {score}</span>
              </div>
            </div>
          )}
        </div>

        {gameOver ? (
          <div className="dino-game-over">
            <div className="game-over-summary">
              <span className="game-over-label">{t("finalScore")}</span>
              <span className="game-over-value">{finalScore}</span>
            </div>
            <HighScores currentScore={finalScore} inline showCurrentScore={false} />
          </div>
        ) : (
          <div className="dino-game-area" onClick={handleAreaClick}>
            <div className="dino-ground"></div>
            <div
              className={`dino ${isJumping ? "jumping" : ""}`}
              style={{
                transform: `translateY(${-dinoY}px) scaleX(-1)`,
                left: `${DINO_X}px`,
              }}
            >
              🦖
            </div>
            {obstacles.map((hole) => {
              const holeWidth = hole.width ?? MIN_HOLE_WIDTH;
              const holeLeft = hole.x - holeWidth;
              return (
                <div
                  key={hole.id}
                  className="obstacle hole"
                  style={{
                    left: `${holeLeft}px`,
                    width: `${holeWidth}px`,
                  }}
                >
                  <div className="hole-visual"></div>
                </div>
              );
            })}
          </div>
        )}

        {!gameStarted && !gameOver && (
          <div className="dino-instructions">
            <p>
              {t("dinoInstructions")
                .split("\n")
                .map((line, index, arr) => (
                  <React.Fragment key={index}>
                    {line}
                    {index < arr.length - 1 && <br />}
                  </React.Fragment>
                ))}
            </p>
            <button className="new-game-button" onClick={resetState}>
              🎮 {t("startGame")}
            </button>
          </div>
        )}

        {gameStarted && !gameOver && (
          <div className="dino-controls">
            <p>{t("jumpHint")}</p>
            <button
              className="dino-jump-button"
              onClick={handleAreaClick}
              onTouchStart={(event) => {
                event.preventDefault();
                handleAreaClick(event);
              }}
              aria-label="Jump"
            >
              ⬆️ {t("jump") || "Jump"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DinoGame;

